// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

// ** Components
import CardsGrid from '@/components/common/CardsGrid';
import ComponentElementsFileCard from '@/components/strapi/Elements/ComponentElementsFileCard';
import { Icon } from '@/components/common/icons';
import { downloadByUrl } from '@/components/common/FileCard';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { FileCardItem } from '@/components/common/FileCard';

type ExtendedFileCardItem = FileCardItem & {
	documentId: string;
	rawFile: any;
	fileName: string;
	isAuthenticated: boolean;
	isLocked: boolean;
	lockStatus: 'public' | 'allowed' | 'unauthenticated' | 'unauthorized';
	actionLabel: string;
	accessRestriction?: string | null;
};

export type FileCardsGridProps = {
	items: ExtendedFileCardItem[];
	currentUser: any;
	fileBlockAuthenticated?: boolean;
	sx?: SxProps<Theme>;
	showAvailabilityCount?: boolean;
	availabilityLabelSuffix?: string;
	emptyStateTitle?: string;
	emptyStateMessage?: string;
	showPreview?: boolean;
	parentResolved?: boolean;
	onSelectAll?: (items: ExtendedFileCardItem[]) => void;
	onDownloadSelected?: (items: ExtendedFileCardItem[]) => void;
};

const SELECT_ALL_LABEL = 'Alle auswählen';
const DESELECT_ALL_LABEL = 'Alle abwählen';

const FileCardsGrid = ({
	items,
	currentUser,
	fileBlockAuthenticated = false,
	sx,
	showAvailabilityCount = false,
	availabilityLabelSuffix = 'Downloads verfügbar',
	emptyStateTitle = 'Keine Downloads gefunden.',
	emptyStateMessage = 'Bitte passen Sie Ihre Suche oder Filter an.',
	showPreview = false,
	parentResolved = false,
	onSelectAll,
	onDownloadSelected
}: FileCardsGridProps) => {
	const theme = useTheme();
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
	const [isDownloading, setIsDownloading] = React.useState(false);

	// 1. Calculate downloadable items safely
	const downloadableItems = React.useMemo(() => {
		return items.filter((item) => !item.isLocked);
	}, [items]);

	// 2. Sync selection state if items change dynamically or get locked out
	React.useEffect(() => {
		const allowedIds = new Set(downloadableItems.map((i) => i.documentId));
		setSelectedIds((prev) => {
			if (prev.size === 0) return prev;

			const next = new Set<string>();
			for (const id of prev) {
				if (allowedIds.has(id)) next.add(id);
			}

			return next.size === prev.size ? prev : next;
		});
	}, [downloadableItems]);

	// 3. Computed Selections
	const selectedItems = React.useMemo(
		() => downloadableItems.filter((it) => selectedIds.has(it.documentId)),
		[downloadableItems, selectedIds]
	);

	const selectedCount = selectedItems.length;
	const isAllSelected = downloadableItems.length > 0 && selectedCount === downloadableItems.length;
	const isActionsDisabled = downloadableItems.length === 0;

	const reservedCtaLabel = isAllSelected ? DESELECT_ALL_LABEL : SELECT_ALL_LABEL;

	// 4. State Handlers
	const handleToggleItem = React.useCallback(
		(nextSelected: boolean, id: string) => {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (nextSelected) {
					const isPermitted = downloadableItems.some((i) => i.documentId === id);
					if (!isPermitted) return prev;
					next.add(id);
				} else {
					next.delete(id);
				}
				return next;
			});
		},
		[downloadableItems]
	);

	const handleSelectAll = React.useCallback(() => {
		if (isActionsDisabled) return;

		const validIds = downloadableItems.map((i) => i.documentId);
		const willDeselectAll = selectedIds.size === validIds.length;
		const nextIds = willDeselectAll ? new Set<string>() : new Set(validIds);

		setSelectedIds(nextIds);

		if (onSelectAll) {
			const callbackItems = items.filter((i) => nextIds.has(i.documentId));
			onSelectAll(callbackItems);
		}
	}, [downloadableItems, isActionsDisabled, onSelectAll, selectedIds.size, items]);

	const handleDownloadSelected = React.useCallback(async () => {
		if (isActionsDisabled || selectedItems.length === 0) return;

		if (onDownloadSelected) {
			onDownloadSelected(selectedItems);
			return;
		}

		if (selectedItems.length === 1) {
			downloadByUrl(selectedItems[0].url, selectedItems[0].fileName);
			setSelectedIds(new Set());
			return;
		}

		setIsDownloading(true);
		try {
			const [JSZipModule, FileSaverModule] = await Promise.all([import('jszip'), import('file-saver')]);
			const JSZip = JSZipModule.default || JSZipModule;
			const { saveAs } = FileSaverModule;
			const zip = new JSZip();

			const results = await Promise.all(
				selectedItems.map(async (item) => {
					try {
						const res = await fetch(item.url);
						if (!res.ok) throw new Error(`HTTP ${res.status}`);
						const blob = await res.blob();
						return { blob, item, failed: false };
					} catch {
						return { blob: null, item, failed: true };
					}
				})
			);

			const usedNames = new Map<string, number>();

			results.forEach(({ blob, item, failed }) => {
				if (!failed && blob) {
					let finalName = item.fileName;
					const hasValidExtension = /\.[a-z0-9]{2,4}$/i.test(finalName);

					if (!hasValidExtension) {
						const urlExtension = item.url.split(/[#?]/)[0].split('.').pop();
						const mimeExtension = blob.type.split('/')[1];

						const extension =
							urlExtension && urlExtension.length < 5
								? urlExtension
								: mimeExtension === 'octet-stream'
									? 'bin'
									: mimeExtension || 'bin';

						finalName = `${finalName}.${extension.replace(/[^a-z0-9]/gi, '')}`;
					}

					const normalizedKey = finalName.toLowerCase();
					if (usedNames.has(normalizedKey)) {
						const count = (usedNames.get(normalizedKey) ?? 0) + 1;
						usedNames.set(normalizedKey, count);

						const dotIndex = finalName.lastIndexOf('.');
						if (dotIndex !== -1) {
							finalName = `${finalName.substring(0, dotIndex)} (${count})${finalName.substring(dotIndex)}`;
						} else {
							finalName = `${finalName} (${count})`;
						}
					} else {
						usedNames.set(normalizedKey, 0);
					}

					zip.file(finalName, blob);
				}
			});

			const content = await zip.generateAsync({ type: 'blob' });
			saveAs(content, 'downloads.zip');
			setSelectedIds(new Set());
		} catch (error) {
			console.error('ZIP generation failed:', error);
		} finally {
			setIsDownloading(false);
		}
	}, [selectedItems, isActionsDisabled, onDownloadSelected]);

	return (
		<Box sx={{ width: '100%' }}>
			<Box
				sx={{
					mb: 6,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
					gap: 8
				}}
			>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
					<ButtonBase
						onClick={handleSelectAll}
						disabled={isActionsDisabled}
						disableRipple
						sx={{
							borderRadius: 1,
							color: !isActionsDisabled ? theme.palette.text.primary : theme.palette.text.disabled,
							transition: 'color 140ms ease',
							'&:hover': {
								color: !isActionsDisabled ? theme.palette.primary.main : undefined
							},
							cursor: !isActionsDisabled ? 'pointer' : 'not-allowed',
							opacity: !isActionsDisabled ? 1 : 0.6
						}}
					>
						<Box sx={{ position: 'relative', display: 'inline-block' }}>
							<Typography component="span" variant="body-md" sx={{ visibility: 'hidden', whiteSpace: 'nowrap' }}>
								{reservedCtaLabel}
							</Typography>
							<Typography
								component="span"
								variant="body-md"
								sx={{
									position: 'absolute',
									left: 0,
									top: 0,
									color: 'inherit',
									whiteSpace: 'nowrap'
								}}
							>
								{isAllSelected ? DESELECT_ALL_LABEL : SELECT_ALL_LABEL}
							</Typography>
						</Box>
					</ButtonBase>

					<Badge
						overlap="circular"
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						invisible={selectedCount === 0 || isActionsDisabled}
						badgeContent={selectedCount}
						sx={{
							'& .MuiBadge-badge': {
								minWidth: 18,
								height: 18,
								top: '-2px',
								right: '-1px',
								borderRadius: 999,
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.common.white,
								fontSize: 12,
								fontWeight: 600,
								boxShadow: `0 6px 18px ${theme.palette.common.black}24`
							}
						}}
					>
						<IconButton
							size="small"
							onClick={handleDownloadSelected}
							disabled={selectedCount === 0 || isDownloading || isActionsDisabled}
							aria-label="Ausgewählte Dateien herunterladen"
							sx={{
								color: theme.palette.text.secondary,
								p: 0.5,
								borderRadius: 999,
								transition:
									'transform 140ms ease, background-color 140ms ease, box-shadow 140ms ease, color 140ms ease',
								'@keyframes downloadWiggle': {
									'0%': { transform: 'translateY(0) scale(1) rotate(0deg)' },
									'35%': {
										transform: 'translateY(-2px) scale(1.06) rotate(-8deg)'
									},
									'70%': {
										transform: 'translateY(0) scale(1.02) rotate(6deg)'
									},
									'100%': { transform: 'translateY(0) scale(1) rotate(0deg)' }
								},
								'&:hover': {
									transform: selectedCount > 0 ? 'translateY(-1px) scale(1.03)' : 'none',
									backgroundColor: selectedCount > 0 ? theme.palette.common.white : 'transparent',
									boxShadow: selectedCount > 0 ? `0 10px 30px ${theme.palette.common.black}14` : 'none'
								},
								'&:hover svg': {
									animation: selectedCount > 0 ? 'downloadWiggle 520ms ease-in-out' : 'none'
								},
								'&:active': {
									transform: selectedCount > 0 ? 'translateY(0) scale(0.99)' : 'none'
								}
							}}
						>
							{isDownloading ? <CircularProgress size={20} color="inherit" /> : <Icon name="download" />}
						</IconButton>
					</Badge>
				</Stack>
			</Box>

			<CardsGrid
				animateOnView={false}
				showAvailabilityCount={showAvailabilityCount}
				availabilityLabelSuffix={availabilityLabelSuffix}
				emptyStateTitle={emptyStateTitle}
				emptyStateMessage={emptyStateMessage}
				sx={[
					{
						gridTemplateColumns: {
							xs: '1fr',
							md: showPreview ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))'
						},
						columnGap: 4,
						rowGap: 6,
						'& > div': {
							width: '100%',
							minWidth: 0,
							justifySelf: 'stretch'
						}
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : [])
				]}
			>
				{items.map((item) => (
					<ComponentElementsFileCard
						key={item.documentId}
						id={item.documentId}
						__typename="ComponentElementsFileCard"
						dataFile={null}
						fileCardIdentifier={item.documentId}
						file={item.rawFile}
						fileName={item.fileName}
						selected={selectedIds.has(item.documentId)}
						onSelectedChange={(nextSelected) => handleToggleItem(nextSelected, item.documentId)}
						currentUser={currentUser}
						fileBlockAuthenticated={fileBlockAuthenticated}
						isAuthenticated={item.isAuthenticated}
						accessRestriction={item.accessRestriction ?? null}
						isLocked={item.isLocked}
						parentResolved={parentResolved}
						showPreview={showPreview}
					/>
				))}
			</CardsGrid>
		</Box>
	);
};

export default FileCardsGrid;
